/**
 * MCPTestHarnessPage - Admin page for testing MCP API endpoints
 * 
 * Allows admins to:
 * - Select Organization → Product → Feature
 * - Test MCP API endpoints without X-SR-Key
 * - Toggle between Test Mode (dry run) and Live Mode (real writes)
 * - View responses in Pretty or Raw JSON format
 */

import React, { useState, useEffect } from 'react';
import Sidebar from '../components/layout/Sidebar';
import Header from '../components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  Plug, Play, Loader2, ChevronDown, ChevronUp, 
  Building2, Package, GitBranch, Code, FileJson, 
  Eye, AlertTriangle, CheckCircle, XCircle,
  ListChecks, Brain, FileText, Activity, GitCommit,
  User, Sparkles, FlaskConical
} from 'lucide-react';
import { toast } from 'sonner';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import { mcpAdminTestApi } from '../services/api';
import PromptSettingsTab from '../components/admin/PromptSettingsTab';

// Endpoint configurations
const ENDPOINTS = [
  {
    id: 'features_ready',
    name: 'List Features Ready',
    method: 'GET',
    path: '/api/v1/mcp/features/ready',
    description: 'Get features that are ready for development (Backlog status, has description). Filter by release to see Kanban board features.',
    icon: ListChecks,
    category: 'read',
    requiresFeature: false,
    params: ['release_id']
  },
  {
    id: 'context',
    name: 'Get MCP Context',
    method: 'GET',
    path: '/api/v1/mcp/context',
    description: 'Pull full product context for AI consumption (DNA, Environment, Constraints)',
    icon: Brain,
    category: 'read',
    requiresFeature: false,
    params: ['depth', 'feature_id']
  },
  {
    id: 'activity',
    name: 'Get Activity Feed',
    method: 'GET',
    path: '/api/v1/mcp/activity',
    description: 'Get activity feed for the product (decisions, status changes, commits)',
    icon: Activity,
    category: 'read',
    requiresFeature: false,
    params: ['limit', 'feature_id']
  },
  {
    id: 'log_decision',
    name: 'Post Technical Decision',
    method: 'POST',
    path: '/api/v1/mcp/features/{id}/log',
    description: 'Log a technical decision, blocker, or criteria completion',
    icon: FileText,
    category: 'write',
    requiresFeature: true
  },
  {
    id: 'update_status',
    name: 'Update Feature Status',
    method: 'PATCH',
    path: '/api/v1/mcp/features/{id}/status',
    description: 'Change feature status (Backlog, In Progress, Review, Done)',
    icon: GitBranch,
    category: 'write',
    requiresFeature: true
  },
  {
    id: 'link_commit',
    name: 'Link Commit to Feature',
    method: 'POST',
    path: '/api/v1/mcp/commits/link',
    description: 'Link a git commit to a feature, optionally marking criteria as addressed',
    icon: GitCommit,
    category: 'write',
    requiresFeature: true
  }
];

const MCPTestHarnessPage = () => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // Selection state
  const [organizations, setOrganizations] = useState([]);
  const [products, setProducts] = useState([]);
  const [features, setFeatures] = useState([]);
  const [releases, setReleases] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedFeature, setSelectedFeature] = useState('');
  const [selectedRelease, setSelectedRelease] = useState('');
  const [selectedDeveloper, setSelectedDeveloper] = useState('');
  
  // UI state
  const [loading, setLoading] = useState({ orgs: true, products: false, features: false, releases: false, developers: false });
  const [testMode, setTestMode] = useState(true);
  const [viewMode, setViewMode] = useState('pretty'); // 'pretty' or 'raw'
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);
  const [runningEndpoint, setRunningEndpoint] = useState(null);
  
  // Results state
  const [results, setResults] = useState({});
  
  // Params for specific endpoints
  const [contextDepth, setContextDepth] = useState('standard');
  const [activityLimit, setActivityLimit] = useState(50);
  const [decisionType, setDecisionType] = useState('decision');
  const [decisionContent, setDecisionContent] = useState('');
  const [decisionCodeSnippet, setDecisionCodeSnippet] = useState('');
  const [newStatus, setNewStatus] = useState('In Progress');
  const [statusComment, setStatusComment] = useState('');
  const [commitHash, setCommitHash] = useState('');
  const [commitMessage, setCommitMessage] = useState('');
  const [filesChanged, setFilesChanged] = useState('');

  // Load organizations on mount
  useEffect(() => {
    loadOrganizations();
  }, []);

  // Load products when org changes
  useEffect(() => {
    if (selectedOrg) {
      loadProducts(selectedOrg);
      setSelectedProduct('');
      setSelectedFeature('');
      setSelectedRelease('');
      setFeatures([]);
      setReleases([]);
      setResults({});
    }
  }, [selectedOrg]);

  // Load features and releases when product changes
  useEffect(() => {
    if (selectedProduct) {
      loadFeatures(selectedProduct);
      loadReleases(selectedProduct);
      loadDevelopers(selectedProduct);
      setSelectedFeature('');
      setSelectedRelease('');
      setSelectedDeveloper('');
      setResults({});
    }
  }, [selectedProduct]);

  const loadOrganizations = async () => {
    try {
      setLoading(prev => ({ ...prev, orgs: true }));
      const res = await mcpAdminTestApi.getOrganizations();
      setOrganizations(res.data.organizations || []);
    } catch (err) {
      toast.error('Failed to load organizations');
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, orgs: false }));
    }
  };

  const loadProducts = async (orgId) => {
    try {
      setLoading(prev => ({ ...prev, products: true }));
      const res = await mcpAdminTestApi.getProducts(orgId);
      setProducts(res.data.products || []);
    } catch (err) {
      toast.error('Failed to load products');
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, products: false }));
    }
  };

  const loadFeatures = async (productId) => {
    try {
      setLoading(prev => ({ ...prev, features: true }));
      const res = await mcpAdminTestApi.getFeatures(productId);
      setFeatures(res.data.features || []);
    } catch (err) {
      toast.error('Failed to load features');
      console.error(err);
    } finally {
      setLoading(prev => ({ ...prev, features: false }));
    }
  };

  const loadReleases = async (productId) => {
    try {
      setLoading(prev => ({ ...prev, releases: true }));
      // Use admin endpoint that bypasses org check
      const res = await mcpAdminTestApi.getReleases(productId);
      // Filter to active/upcoming releases
      const now = new Date();
      const activeReleases = (res.data.releases || []).filter(r => {
        const endDate = r.end_date ? new Date(r.end_date) : null;
        return !endDate || endDate >= now;
      });
      setReleases(activeReleases);
    } catch (err) {
      console.error('Failed to load releases:', err);
      setReleases([]);
    } finally {
      setLoading(prev => ({ ...prev, releases: false }));
    }
  };

  const loadDevelopers = async (productId) => {
    try {
      setLoading(prev => ({ ...prev, developers: true }));
      const res = await mcpAdminTestApi.getTeamMembersWithKeys(productId);
      setDevelopers(res.data.team_members || []);
    } catch (err) {
      console.error('Failed to load developers:', err);
      setDevelopers([]);
    } finally {
      setLoading(prev => ({ ...prev, developers: false }));
    }
  };

  const runEndpoint = async (endpoint) => {
    if (!selectedProduct) {
      toast.error('Please select a product first');
      return;
    }

    if (endpoint.requiresFeature && !selectedFeature) {
      toast.error('Please select a feature for this endpoint');
      return;
    }

    setRunningEndpoint(endpoint.id);

    try {
      let response;
      
      switch (endpoint.id) {
        case 'features_ready':
          response = await mcpAdminTestApi.testFeaturesReady(
            selectedProduct, 
            selectedRelease || null,
            selectedDeveloper || null
          );
          break;
        case 'context':
          response = await mcpAdminTestApi.testContext(
            selectedProduct, 
            contextDepth, 
            selectedFeature || null
          );
          break;
        case 'activity':
          response = await mcpAdminTestApi.testActivity(
            selectedProduct,
            activityLimit,
            selectedFeature || null
          );
          break;
        case 'log_decision':
          response = await mcpAdminTestApi.testLogDecision(
            selectedFeature,
            selectedProduct,
            {
              type: decisionType,
              content: decisionContent,
              code_snippet: decisionCodeSnippet || null
            },
            testMode
          );
          break;
        case 'update_status':
          response = await mcpAdminTestApi.testUpdateStatus(
            selectedFeature,
            selectedProduct,
            {
              status: newStatus,
              comment: statusComment || null
            },
            testMode
          );
          break;
        case 'link_commit':
          response = await mcpAdminTestApi.testLinkCommit(
            {
              commit_hash: commitHash,
              message: commitMessage,
              feature_id: selectedFeature,
              files_changed: filesChanged ? filesChanged.split(',').map(f => f.trim()) : [],
              criteria_addressed: []
            },
            testMode
          );
          break;
        default:
          throw new Error('Unknown endpoint');
      }

      setResults(prev => ({
        ...prev,
        [endpoint.id]: {
          success: true,
          data: response.data,
          timestamp: new Date().toISOString()
        }
      }));
      
      toast.success(`${endpoint.name} executed successfully`);
    } catch (err) {
      setResults(prev => ({
        ...prev,
        [endpoint.id]: {
          success: false,
          error: err.response?.data?.detail || err.message,
          timestamp: new Date().toISOString()
        }
      }));
      toast.error(`${endpoint.name} failed`);
    } finally {
      setRunningEndpoint(null);
    }
  };

  const renderResultPretty = (result, endpoint) => {
    if (!result) return null;
    
    if (!result.success) {
      return (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 mb-2">
            <XCircle className="w-4 h-4" />
            <span className="font-medium">Error</span>
          </div>
          <p className="text-sm text-red-700 dark:text-red-300">{result.error}</p>
        </div>
      );
    }

    const data = result.data;
    
    return (
      <div className="space-y-3">
        {/* Endpoint info */}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <span className="font-mono">{data.endpoint}</span>
          {data.test_mode !== undefined && (
            <Badge variant={data.test_mode ? 'secondary' : 'default'}>
              {data.test_mode ? 'Test Mode' : 'Live Mode'}
            </Badge>
          )}
        </div>

        {/* Response content */}
        {data.response !== undefined && (
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 border">
            {/* Features ready - handles both empty and populated arrays */}
            {Array.isArray(data.response) && (
              <div className="space-y-2">
                {data.response.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">
                    No features ready for development. Features need: Backlog/Todo status and a description.
                  </p>
                ) : data.response[0]?.id ? (
                  <>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {data.response.length} feature(s) ready for development:
                    </p>
                    {data.response.map((feat, i) => (
                      <div key={i} className="p-3 bg-white dark:bg-gray-800 rounded border">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{feat.status}</Badge>
                          <span className="font-medium">{feat.title}</span>
                        </div>
                        {feat.epic?.title && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Epic: {feat.epic.title}</p>
                        )}
                        {feat.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{feat.description}</p>
                        )}
                        {feat.acceptance_criteria?.length > 0 && (
                          <p className="text-xs text-gray-500 mt-1">
                            {feat.acceptance_criteria.length} acceptance criteria
                          </p>
                        )}
                      </div>
                    ))}
                  </>
                ) : null}
              </div>
            )}

            {/* Activity feed */}
            {data.response?.activities && (
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  {data.response.activities.length} activity item(s):
                </p>
                {data.response.activities.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">No activity found</p>
                ) : (
                  data.response.activities.slice(0, 10).map((act, i) => (
                    <div key={i} className="p-2 bg-white dark:bg-gray-800 rounded border text-sm">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">{act.type}</Badge>
                        <span className="text-gray-500 text-xs">
                          {new Date(act.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="mt-1">{act.content}</p>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Context response */}
            {data.response?.product && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-gray-500">Product</Label>
                  <p className="font-medium">{data.response.product.name}</p>
                </div>
                {data.response.ai_constraints && (
                  <div>
                    <Label className="text-xs text-gray-500">
                      AI Constraints ({data.response.ai_constraints.length})
                    </Label>
                    <div className="mt-1 space-y-1">
                      {data.response.ai_constraints.slice(0, 5).map((c, i) => (
                        <p key={i} className="text-sm font-mono bg-white dark:bg-gray-800 p-2 rounded border">
                          {c}
                        </p>
                      ))}
                      {data.response.ai_constraints.length > 5 && (
                        <p className="text-xs text-gray-500">
                          ...and {data.response.ai_constraints.length - 5} more
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Status/decision responses */}
            {(data.response?.status === 'logged' || data.response?.new_status) && (
              <div className="text-green-600 dark:text-green-400">
                <CheckCircle className="w-4 h-4 inline mr-2" />
                {data.response.status === 'logged' 
                  ? `Logged as ${data.response.activity_id}` 
                  : `Status changed: ${data.response.previous_status} → ${data.response.new_status}`
                }
              </div>
            )}

            {/* Test mode preview */}
            {data.would_log && (
              <div className="space-y-2">
                <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Preview (not saved):
                </p>
                <pre className="text-xs bg-white dark:bg-gray-800 p-3 rounded border overflow-auto">
                  {JSON.stringify(data.would_log, null, 2)}
                </pre>
              </div>
            )}
            {data.would_change && (
              <div className="space-y-2">
                <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Preview (not saved):
                </p>
                <pre className="text-xs bg-white dark:bg-gray-800 p-3 rounded border overflow-auto">
                  {JSON.stringify(data.would_change, null, 2)}
                </pre>
              </div>
            )}
            {data.would_link && (
              <div className="space-y-2">
                <p className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Preview (not saved):
                </p>
                <pre className="text-xs bg-white dark:bg-gray-800 p-3 rounded border overflow-auto">
                  {JSON.stringify(data.would_link, null, 2)}
                </pre>
              </div>
            )}

            {/* Fallback: Show raw response if no pretty formatter matched */}
            {data.response && 
             !Array.isArray(data.response) && 
             !data.response?.activities && 
             !data.response?.product && 
             !(data.response?.status === 'logged' || data.response?.new_status) && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">Response:</p>
                <pre className="text-xs bg-white dark:bg-gray-800 p-3 rounded border overflow-auto max-h-[300px]">
                  {JSON.stringify(data.response, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderResultRaw = (result) => {
    if (!result) return null;
    
    return (
      <ScrollArea className="h-[400px]">
        <pre className="text-xs font-mono bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto">
          {JSON.stringify(result.data || result.error, null, 2)}
        </pre>
      </ScrollArea>
    );
  };

  return (
    <div className={cn("flex h-screen", colors.bgPrimary)}>
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-auto p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div>
              <h1 className={cn("text-2xl font-bold flex items-center gap-2", colors.textPrimary)}>
                <Plug className="w-6 h-6" />
                MCP Admin
              </h1>
              <p className={cn("text-sm mt-1", colors.textSecondary)}>
                Configure AI prompts and test MCP endpoints. Admin access only.
              </p>
            </div>

            {/* Main Tabs */}
            <Tabs defaultValue="prompt-settings" className="space-y-4">
              <TabsList className="bg-white dark:bg-gray-800 border shadow-sm">
                <TabsTrigger 
                  value="prompt-settings" 
                  className="data-[state=active]:bg-blue-500 data-[state=active]:text-white"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Prompt Settings
                </TabsTrigger>
                <TabsTrigger 
                  value="test-harness" 
                  className="data-[state=active]:bg-blue-500 data-[state=active]:text-white"
                >
                  <FlaskConical className="w-4 h-4 mr-2" />
                  Test Harness
                </TabsTrigger>
              </TabsList>

              {/* Prompt Settings Tab */}
              <TabsContent value="prompt-settings">
                <PromptSettingsTab />
              </TabsContent>

              {/* Test Harness Tab */}
              <TabsContent value="test-harness" className="space-y-6">
                {/* Selection Panel */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Select Context</CardTitle>
                    <CardDescription>
                      Choose an organization, product, and optionally a feature to test against
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-5 gap-4">
                      {/* Organization */}
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          Organization
                        </Label>
                    <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                      <SelectTrigger data-testid="select-org">
                        <SelectValue placeholder={loading.orgs ? "Loading..." : "Select organization"} />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.organization_id} value={org.organization_id}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Product */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Package className="w-4 h-4" />
                      Product
                    </Label>
                    <Select 
                      value={selectedProduct} 
                      onValueChange={setSelectedProduct}
                      disabled={!selectedOrg}
                    >
                      <SelectTrigger data-testid="select-product">
                        <SelectValue placeholder={
                          loading.products ? "Loading..." : 
                          !selectedOrg ? "Select org first" : 
                          "Select product"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((prod) => (
                          <SelectItem key={prod.product_id} value={prod.product_id}>
                            <div className="flex items-center gap-2">
                              {prod.name}
                              {prod.delivery_config?.config_complete && (
                                <Badge variant="outline" className="text-xs">MCP Ready</Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Release (Optional - for filtering features) */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Activity className="w-4 h-4" />
                      Release
                    </Label>
                    <Select 
                      value={selectedRelease || '__none__'} 
                      onValueChange={(v) => setSelectedRelease(v === '__none__' ? '' : v)}
                      disabled={!selectedProduct}
                    >
                      <SelectTrigger data-testid="select-release">
                        <SelectValue placeholder={
                          loading.releases ? "Loading..." : 
                          !selectedProduct ? "Select product first" : 
                          "Active (auto)"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Active (auto-detect)</SelectItem>
                        {releases.map((rel) => {
                          const isActive = rel.start_date && new Date(rel.start_date) <= new Date();
                          const displayName = rel.title || rel.name || `Release ${rel.release_id.slice(-6)}`;
                          return (
                            <SelectItem key={rel.release_id} value={rel.release_id}>
                              <div className="flex items-center gap-2">
                                {displayName}
                                {isActive && (
                                  <Badge variant="default" className="text-xs">Active</Badge>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Developer (Simulate as specific team member) */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Developer
                    </Label>
                    <Select 
                      value={selectedDeveloper || '__none__'} 
                      onValueChange={(v) => setSelectedDeveloper(v === '__none__' ? '' : v)}
                      disabled={!selectedProduct}
                    >
                      <SelectTrigger data-testid="select-developer">
                        <SelectValue placeholder={
                          loading.developers ? "Loading..." : 
                          !selectedProduct ? "Select product first" : 
                          developers.length === 0 ? "No keys issued" :
                          "All developers"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">All developers</SelectItem>
                        {developers.map((dev) => (
                          <SelectItem key={dev.member_id} value={dev.member_id}>
                            {dev.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Feature */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <GitBranch className="w-4 h-4" />
                      Feature (optional)
                    </Label>
                    <Select 
                      value={selectedFeature || '__none__'} 
                      onValueChange={(v) => setSelectedFeature(v === '__none__' ? '' : v)}
                      disabled={!selectedProduct}
                    >
                      <SelectTrigger data-testid="select-feature">
                        <SelectValue placeholder={
                          loading.features ? "Loading..." : 
                          !selectedProduct ? "Select product first" : 
                          "Select feature"
                        } />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None (product-level)</SelectItem>
                        {features.map((feat) => (
                          <SelectItem key={feat.feature_id} value={feat.feature_id}>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-xs">{feat.status}</Badge>
                              {feat.title}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Mode Toggle */}
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="test-mode"
                      checked={testMode}
                      onCheckedChange={setTestMode}
                    />
                    <Label htmlFor="test-mode" className="flex items-center gap-2 cursor-pointer">
                      {testMode ? (
                        <>
                          <AlertTriangle className="w-4 h-4 text-amber-500" />
                          <span>Test Mode</span>
                          <span className="text-xs text-gray-500">(dry run, no writes)</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Live Mode</span>
                          <span className="text-xs text-red-500">(writes to database!)</span>
                        </>
                      )}
                    </Label>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-gray-500">View:</Label>
                    <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
                      <TabsList className="h-8">
                        <TabsTrigger value="pretty" className="text-xs px-2 h-6">
                          <Eye className="w-3 h-3 mr-1" />
                          Pretty
                        </TabsTrigger>
                        <TabsTrigger value="raw" className="text-xs px-2 h-6">
                          <FileJson className="w-3 h-3 mr-1" />
                          Raw JSON
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Endpoints */}
            <div className="space-y-3">
              <h2 className={cn("text-lg font-semibold", colors.textPrimary)}>
                MCP Endpoints
              </h2>
              
              {/* Read Endpoints */}
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Read Operations
                </h3>
                {ENDPOINTS.filter(e => e.category === 'read').map((endpoint) => (
                  <EndpointCard
                    key={endpoint.id}
                    endpoint={endpoint}
                    expanded={expandedEndpoint === endpoint.id}
                    onToggle={() => setExpandedEndpoint(
                      expandedEndpoint === endpoint.id ? null : endpoint.id
                    )}
                    running={runningEndpoint === endpoint.id}
                    onRun={() => runEndpoint(endpoint)}
                    result={results[endpoint.id]}
                    viewMode={viewMode}
                    renderPretty={renderResultPretty}
                    renderRaw={renderResultRaw}
                    canRun={!!selectedProduct}
                    // Params
                    contextDepth={contextDepth}
                    setContextDepth={setContextDepth}
                    activityLimit={activityLimit}
                    setActivityLimit={setActivityLimit}
                    selectedFeature={selectedFeature}
                  />
                ))}
              </div>

              {/* Write Endpoints */}
              <div className="space-y-2 mt-6">
                <h3 className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <Code className="w-4 h-4" />
                  Write Operations
                  {!testMode && (
                    <Badge variant="destructive" className="text-xs">LIVE</Badge>
                  )}
                </h3>
                {ENDPOINTS.filter(e => e.category === 'write').map((endpoint) => (
                  <EndpointCard
                    key={endpoint.id}
                    endpoint={endpoint}
                    expanded={expandedEndpoint === endpoint.id}
                    onToggle={() => setExpandedEndpoint(
                      expandedEndpoint === endpoint.id ? null : endpoint.id
                    )}
                    running={runningEndpoint === endpoint.id}
                    onRun={() => runEndpoint(endpoint)}
                    result={results[endpoint.id]}
                    viewMode={viewMode}
                    renderPretty={renderResultPretty}
                    renderRaw={renderResultRaw}
                    canRun={!!selectedProduct && (!endpoint.requiresFeature || !!selectedFeature)}
                    testMode={testMode}
                    // Params
                    decisionType={decisionType}
                    setDecisionType={setDecisionType}
                    decisionContent={decisionContent}
                    setDecisionContent={setDecisionContent}
                    decisionCodeSnippet={decisionCodeSnippet}
                    setDecisionCodeSnippet={setDecisionCodeSnippet}
                    newStatus={newStatus}
                    setNewStatus={setNewStatus}
                    statusComment={statusComment}
                    setStatusComment={setStatusComment}
                    commitHash={commitHash}
                    setCommitHash={setCommitHash}
                    commitMessage={commitMessage}
                    setCommitMessage={setCommitMessage}
                    filesChanged={filesChanged}
                    setFilesChanged={setFilesChanged}
                    selectedFeature={selectedFeature}
                  />
                ))}
              </div>
            </div>
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </div>
  );
};

// Endpoint Card Component
const EndpointCard = ({
  endpoint,
  expanded,
  onToggle,
  running,
  onRun,
  result,
  viewMode,
  renderPretty,
  renderRaw,
  canRun,
  testMode,
  // Read params
  contextDepth,
  setContextDepth,
  activityLimit,
  setActivityLimit,
  selectedFeature,
  // Write params
  decisionType,
  setDecisionType,
  decisionContent,
  setDecisionContent,
  decisionCodeSnippet,
  setDecisionCodeSnippet,
  newStatus,
  setNewStatus,
  statusComment,
  setStatusComment,
  commitHash,
  setCommitHash,
  commitMessage,
  setCommitMessage,
  filesChanged,
  setFilesChanged
}) => {
  const Icon = endpoint.icon;
  
  return (
    <Card className={cn(
      "transition-all",
      expanded && "ring-2 ring-blue-500/20"
    )}>
      <Collapsible open={expanded} onOpenChange={onToggle}>
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5 text-gray-500" />
                <div>
                  <div className="flex items-center gap-2">
                    <Badge 
                      variant={endpoint.method === 'GET' ? 'secondary' : 'default'}
                      className="text-xs"
                    >
                      {endpoint.method}
                    </Badge>
                    <span className="font-medium">{endpoint.name}</span>
                    {endpoint.requiresFeature && (
                      <Badge variant="outline" className="text-xs">Requires Feature</Badge>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 font-mono">{endpoint.path}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {result && (
                  result.success 
                    ? <CheckCircle className="w-4 h-4 text-green-500" />
                    : <XCircle className="w-4 h-4 text-red-500" />
                )}
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </div>
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0 border-t">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {endpoint.description}
            </p>

            {/* Endpoint-specific params */}
            {endpoint.id === 'context' && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Depth</Label>
                  <Select value={contextDepth} onValueChange={setContextDepth}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="minimal">Minimal (~5KB)</SelectItem>
                      <SelectItem value="standard">Standard (~25KB)</SelectItem>
                      <SelectItem value="full">Full (~100KB max)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Feature Filter</Label>
                  <Input 
                    value={selectedFeature || ''} 
                    disabled 
                    placeholder="Uses selected feature above"
                  />
                </div>
              </div>
            )}

            {endpoint.id === 'activity' && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  <Label>Limit</Label>
                  <Input 
                    type="number" 
                    value={activityLimit} 
                    onChange={(e) => setActivityLimit(parseInt(e.target.value) || 50)}
                    min={1}
                    max={100}
                  />
                </div>
              </div>
            )}

            {endpoint.id === 'log_decision' && (
              <div className="space-y-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={decisionType} onValueChange={setDecisionType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="decision">Decision</SelectItem>
                        <SelectItem value="blocker">Blocker</SelectItem>
                        <SelectItem value="criteria_complete">Criteria Complete</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Content *</Label>
                  <Textarea 
                    value={decisionContent}
                    onChange={(e) => setDecisionContent(e.target.value)}
                    placeholder="Describe the technical decision..."
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Code Snippet (optional)</Label>
                  <Textarea 
                    value={decisionCodeSnippet}
                    onChange={(e) => setDecisionCodeSnippet(e.target.value)}
                    placeholder="const example = 'code';"
                    rows={2}
                    className="font-mono text-sm"
                  />
                </div>
              </div>
            )}

            {endpoint.id === 'update_status' && (
              <div className="space-y-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>New Status *</Label>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Backlog">Backlog</SelectItem>
                        <SelectItem value="Todo">Todo</SelectItem>
                        <SelectItem value="In Progress">In Progress</SelectItem>
                        <SelectItem value="Review">Review</SelectItem>
                        <SelectItem value="Done">Done</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Comment (optional)</Label>
                  <Input 
                    value={statusComment}
                    onChange={(e) => setStatusComment(e.target.value)}
                    placeholder="Reason for status change..."
                  />
                </div>
              </div>
            )}

            {endpoint.id === 'link_commit' && (
              <div className="space-y-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Commit Hash *</Label>
                    <Input 
                      value={commitHash}
                      onChange={(e) => setCommitHash(e.target.value)}
                      placeholder="a1b2c3d4e5f6"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Files Changed (comma-separated)</Label>
                    <Input 
                      value={filesChanged}
                      onChange={(e) => setFilesChanged(e.target.value)}
                      placeholder="src/auth.ts, src/utils.ts"
                      className="font-mono text-sm"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Commit Message *</Label>
                  <Input 
                    value={commitMessage}
                    onChange={(e) => setCommitMessage(e.target.value)}
                    placeholder="[feat_xxx] Add password validation"
                  />
                </div>
              </div>
            )}

            {/* Run Button */}
            <div className="flex items-center gap-3">
              <Button
                onClick={onRun}
                disabled={!canRun || running}
                variant={endpoint.category === 'write' && !testMode ? 'destructive' : 'default'}
                data-testid={`run-${endpoint.id}`}
              >
                {running ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                {running ? 'Running...' : 'Run'}
              </Button>
              {!canRun && (
                <span className="text-sm text-gray-500">
                  {endpoint.requiresFeature ? 'Select a feature to run' : 'Select a product to run'}
                </span>
              )}
            </div>

            {/* Results */}
            {result && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-gray-500">
                    Response ({new Date(result.timestamp).toLocaleTimeString()})
                  </Label>
                </div>
                {viewMode === 'pretty' ? renderPretty(result, endpoint) : renderRaw(result)}
              </div>
            )}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default MCPTestHarnessPage;
